import React, { useEffect } from 'react';
import _ from 'underscore';
import {
  Button,
  mergeClasses,
  OverflowItem,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  MenuList,
  MenuItem,
  Label,
} from '@fluentui/react-components';
import { ChevronRight20Regular, ChevronDown20Regular } from '@fluentui/react-icons';

import { SideNavigationAccordionProps, SideNavigationSecondaryItemProps } from './types';
// eslint-disable-next-line import/no-cycle
import { SideNavigationItem } from './side-navigation-item';
import { defaultConfig } from './utilities';
import { useSiteNavigationItemPopoverStyles, usePopOverContainerStyles } from './styles';

export const SideNavigationAccordion : React.FC<SideNavigationAccordionProps> = ({
  item,
  isCollapsed = false,
  secondaryNavItems,
  className,
  mode,
  config,
  onTabSelect,
  selectedTabId,
  groupId,
}) => {
  const styles = useSiteNavigationItemPopoverStyles();
  const popoverStyle = usePopOverContainerStyles();
  const [open, setOpen] = React.useState(!isCollapsed);
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const onClick = () => {
    setOpen(!open);
    item.onClick?.();
    setPopoverOpen(false);
  };
  const {
    popOverSettings,
  } = config;
  const {
    popOverMouseLeaveDelay,
    withArrow,
    openOnHover,
  } = _.defaults(popOverSettings, defaultConfig.popOverSettings);

  useEffect(() => {
    const itemSelected = _.some(secondaryNavItems, (secondaryItem) => secondaryItem.isSelected);

    if (itemSelected) {
      setOpen(true);
    }
  }, [secondaryNavItems]);

  useEffect(() => {
    if (open) {
      setPopoverOpen(false);
    }
  }, [open]);

  const Icon = open === false ? ChevronRight20Regular : ChevronDown20Regular;

  const onItemClick = (secondaryItem: SideNavigationSecondaryItemProps) => {
    onTabSelect(item.key);
    secondaryItem.onClick?.();
    setPopoverOpen(false);
  };

  return (
    <div key={`tab_accordion_${item.key}`}>
      <Popover
        open={popoverOpen}
        openOnHover={openOnHover}
        withArrow={withArrow}
        mouseLeaveDelay={popOverMouseLeaveDelay}
        positioning="after"
        onOpenChange={(e, data) => {
          setPopoverOpen(data.open && !open);
        }}
      >
        <OverflowItem
          key={`overflow_${item.key}`}
          id={item.id}
          groupId={groupId}
        >
          <PopoverTrigger disableButtonEnhancement>
            <Button icon={<Icon />} appearance="subtle" onClick={onClick} className={mergeClasses(styles.accordionButton, 'side-navigation-item-accordion')}>
              {item.text}
            </Button>
          </PopoverTrigger>
        </OverflowItem>
        <PopoverSurface className={popoverStyle.popOverContainer}>
          <div className={styles.popOverTitleContainer}>
            <Label className={styles.popOverTitle}>{item.text}</Label>
          </div>
          { secondaryNavItems.length > 0 && (
          <MenuList className={mergeClasses('side-navigation-sub-menu-items', styles.menu)}>
            {secondaryNavItems.map((secondaryItem) => (
              <MenuItem
                key={`${item.key}_${secondaryItem.text}`}
                className={mergeClasses('side-navigation-sub-menu-item', styles.menuButton)}
                onClick={() => onItemClick(secondaryItem)}
                id={secondaryItem.id}
              >
                {secondaryItem.text}
              </MenuItem>
            ))}
          </MenuList>
          )}
        </PopoverSurface>
      </Popover>
      {
        open
        && secondaryNavItems
          .map((secondaryItem) => (
            <SideNavigationItem
              key={`tab_accordion_item_${secondaryItem.key}`}
              item={secondaryItem}
              mode={mode}
              className={className}
              config={config}
              groupId={groupId}
              selectedTabId={selectedTabId}
              onTabSelect={onTabSelect}
            />
          ))
      }
    </div>
  );
};
